'use client';

import { memo } from 'react';
import { BellIcon, EllipsisVerticalIcon, PencilIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'next-i18next';
import { Anchor } from '@components/common';
import { cn } from '@utils/cn';
import { getI18nLinkHref } from '@services/i18nPaths';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@components/ui/dropdown-menu';

interface CompactJobCardProps {
  job: ApiSchema<'McMyJobCard'>;
  isSelected?: boolean;
  onSelect?: (job: ApiSchema<'McMyJobCard'>) => void;
  /** When provided, the NEW button calls this with status=1 (new applicants) instead of navigating. */
  onTagClick?: (job: ApiSchema<'McMyJobCard'>, status: number) => void;
  /** Optional href builder — used in MC context to produce /[locale]/mc/... URLs. */
  buildTagHref?: (jobSlug: string, status: number) => string;
  /** Locale used to build edit and applicant URLs. When provided, EDIT and NEW buttons are shown. */
  locale?: Locale;
}

const TRIGGER_BTN_CLASS = 'flex size-8 items-center justify-center rounded-2xl text-ink opacity-0 transition hover:bg-surface-300 group-hover:opacity-100 data-[state=open]:bg-surface-300 data-[state=open]:opacity-100';
const MENU_ITEM_CLASS = 'cursor-pointer gap-2 px-3 py-2 text-sm text-ink hover:!bg-surface-200 focus:!bg-surface-200 focus:text-ink';
const MENU_ITEM_DISABLED_CLASS = 'gap-2 px-3 py-2 text-sm text-ink/40';

/**
 * @description Compact job view for the company jobs sidebar.
 * Shows job title (with an inline new-applicants badge so the count stays
 * visible at a glance) and a "more" menu with EDIT and NEW actions.
 */
const CompactJobCard = (props: CompactJobCardProps) => {
  const {
    job, isSelected, onSelect, onTagClick, buildTagHref, locale,
  } = props;

  const { t } = useTranslation('common');
  const flowHref = locale ? getI18nLinkHref(`/mc/job/${job.slug}/applicants/input-flow`, locale) : `/mc/job/${job.slug}/applicants/input-flow`;

  const handleNew = () => {
    if (onTagClick) {
      onTagClick(job, 1);
    }
  };

  const newCount = job.statusCounts?.unprocessed ?? 0;
  const hasNewApplicants = newCount > 0;
  const newTitle = t('mc.sidebar.unprocessed.applicants').replace('%count%', String(newCount));

  const jobHoverParts = [job.title, job.location, job.activatedAt].filter(Boolean);
  const jobHoverTitle = jobHoverParts.length > 0 ? jobHoverParts.join('\n') : undefined;

  const newApplicantsHref = buildTagHref
    ? buildTagHref(job.slug!, 1)
    : flowHref;

  const titleBadge = hasNewApplicants ? (
    <span
      title={newTitle}
      className="flex size-4 shrink-0 items-center justify-center rounded-full bg-ink text-[10px] font-bold text-surface"
    >
      {newCount}
    </span>
  ) : null;

  let bellMenuItem: React.ReactNode = null;
  if (locale && job.slug) {
    if (!hasNewApplicants) {
      bellMenuItem = (
        <DropdownMenuItem disabled className={MENU_ITEM_DISABLED_CLASS}>
          <BellIcon className="size-5" />
          <span>{newTitle}</span>
        </DropdownMenuItem>
      );
    } else {
      // Anchor-backed item so the ?status=1 link is visible to the browser.
      // When onTagClick is provided (MC soft-routing), it intercepts the click and prevents native nav.
      bellMenuItem = (
        <DropdownMenuItem asChild className={MENU_ITEM_CLASS}>
          <Anchor
            href={newApplicantsHref}
            styling="none"
            onClick={(e) => {
              e.stopPropagation();
              if (onTagClick) {
                e.preventDefault();
                handleNew();
              }
            }}
          >
            <BellIcon className="size-5 text-ink" />
            <span>{newTitle}</span>
          </Anchor>
        </DropdownMenuItem>
      );
    }
  }

  return (
    <div
      className={cn(
        'group flex items-center gap-2 rounded-2xl px-3 py-1 transition-colors hover:bg-surface-150',
        isSelected && 'bg-primary/5',
      )}
    >
      {/* Job title with optional new-applicants badge — fills remaining space so the trigger right-aligns; truncates with ellipsis */}
      <Anchor
        styling="none"
        href={flowHref}
        className="flex min-w-0 flex-1 items-center gap-1.5"
        title={jobHoverTitle}
        onClick={(e) => {
          if (onSelect) {
            // In Mission Control context: delegate navigation to the parent handler
            e.preventDefault();
            onSelect(job);
          } else {
            e.stopPropagation();
          }
        }}
      >
        <span
          className={cn(
            'min-w-0 truncate text-sm text-ink',
            isSelected && 'text-primary',
          )}
        >
          {job.title}
        </span>
        {titleBadge}
      </Anchor>

      {/* "More" menu — folds EDIT and NEW into a single popover */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={t('global.label.actions')}
            title={t('global.label.actions')}
            className={TRIGGER_BTN_CLASS}
            onClick={(e) => e.stopPropagation()}
          >
            <EllipsisVerticalIcon className="size-5" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" side="bottom" sideOffset={4} className="z-[90] min-w-48">
          {locale && job.slug && (
            <DropdownMenuItem asChild className={MENU_ITEM_CLASS}>
              <Anchor
                href={`/${locale}/mc/job-add/edit/${job.slug}`}
                styling="none"
                onClick={(e) => e.stopPropagation()}
              >
                <PencilIcon className="size-5 text-ink" />
                <span>{t('job.edit.title')}</span>
              </Anchor>
            </DropdownMenuItem>
          )}

          {/* NEW — opens new applicants folder (status=1); disabled when there are none */}
          {bellMenuItem}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export const MemoizedCompactJobCard = memo(CompactJobCard);
